import { keyframes } from '@emotion/react'

import type { SideMenuDot } from '~/models/sideMenuDot'

/**
 * design #9306 ③ (2026-10-02 デザイナー決定): ドットのある行の地色を 2 秒でゆっくり明滅させる
 * (`design/css/layout.css` の `--nav-pulse-*` / `navRowPulse*`)。選択中の行も警告色に置き換わる。
 * 動きを減らす設定でも止めない。
 *
 * 色は token の参照 (`{colors.…}`) から導く。ライト・ダークの値の差は design どおり
 * `_dark` で切り替える (現在のアプリは `forcedTheme="light"`)。
 */
const frames = keyframes`
  0%, 100% { background: var(--side-menu-dot-pulse-lo); }
  50% { background: var(--side-menu-dot-pulse-hi); }
`

const PULSE_VARS = {
  warning: {
    '--side-menu-dot-pulse-lo': '{colors.Surface.warningMid}',
    '--side-menu-dot-pulse-hi':
      'color-mix(in srgb, {colors.Object.warningMid} 65%, {colors.Surface.warningMid})',
    _dark: {
      '--side-menu-dot-pulse-lo': '{colors.Surface.warningLow}',
      '--side-menu-dot-pulse-hi':
        'color-mix(in srgb, {colors.Surface.warningMid} 45%, {colors.Surface.warningLow})',
    },
  },
  negative: {
    '--side-menu-dot-pulse-lo': '{colors.Surface.negativeMid}',
    '--side-menu-dot-pulse-hi':
      'color-mix(in srgb, {colors.Object.negativeMid} 45%, {colors.Surface.negativeMid})',
    _dark: {
      // 赤はドット自体が暗いので、地色を *-low より下 (サイドメニューの地色との中間) から始める
      '--side-menu-dot-pulse-lo':
        'color-mix(in srgb, {colors.Surface.negativeLow} 55%, {colors.Surface.secondary})',
      '--side-menu-dot-pulse-hi': '{colors.Surface.negativeLow}',
    },
  },
} as const

/** 行 (ドットを子に持つ要素) に当てる style。 */
export const sideMenuRowPulseStyle = (
  severity: SideMenuDot['severity'],
): Record<string, unknown> => ({
  ...PULSE_VARS[severity],
  background: 'var(--side-menu-dot-pulse-lo)',
  animation: `${frames} 2s ease-in-out infinite`,
})

/** ドットが行の明滅の中にいることを示す data 属性 (selector と test の目印)。 */
export const SIDE_MENU_DOT_ATTR = 'data-side-menu-dot'
